import { forwardRef, useEffect, useState } from 'react';

import type { GetUserQuestionAnswersRankingResponse } from '@genially/contracts';

import { UserAliasAvatar } from '../../../../../../../UserAliasAvatar/UserAliasAvatar';
import { PositionChange } from '../../../../../../hooks/useCalculateRankingPositionChanges';
import { CHANGE_POSITION_TIMEOUT } from '../../constants';
import { StyledMedalIcon } from '../Common.styled';
import {
  ChangePositionIconWrapper,
  DataText,
  OrderNumber,
  StyledDisplayDownIcon,
  StyledDisplayUpIcon,
  UserAlias,
  Wrapper,
} from './RankingItem.styled';

interface RankingItemProps {
  question: GetUserQuestionAnswersRankingResponse[0];
  index: number;
  isCurrentUser: boolean;
  medalPosition?: 'first' | 'second' | 'third';
  zIndex: number;
  positionChange: PositionChange;
  enterAnimation?: boolean;
  enterDelayAnimation?: number;
  onChangePositionTimeout: () => void;
}

export const RankingItem = forwardRef<HTMLDivElement, RankingItemProps>(
  (
    {
      question,
      index,
      isCurrentUser,
      medalPosition,
      zIndex,
      positionChange,
      enterAnimation,
      enterDelayAnimation,
      onChangePositionTimeout,
      ...rest
    },
    ref,
  ) => {
    const [displayChangePositionTimeout, setDisplayChangePositionTimeout] =
      useState<NodeJS.Timeout | null>(null);
    const [internalPositionChange, setInternalPositionChange] = useState<PositionChange>(
      PositionChange.SAME,
    );

    useEffect(() => {
      if (positionChange !== PositionChange.SAME) {
        setInternalPositionChange(positionChange);
        setDisplayChangePositionTimeout(
          setTimeout(() => {
            setInternalPositionChange(PositionChange.SAME);
            setDisplayChangePositionTimeout(null);
            onChangePositionTimeout();
          }, CHANGE_POSITION_TIMEOUT),
        );
      }

      return () => {
        if (displayChangePositionTimeout) {
          clearTimeout(displayChangePositionTimeout);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onChangePositionTimeout, positionChange]);

    return (
      <Wrapper
        ref={ref}
        $isSelected={isCurrentUser}
        $zIndex={zIndex}
        $activeAnimation={enterAnimation}
        $delayAnimation={enterDelayAnimation}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
      >
        <OrderNumber>
          {medalPosition ? (
            <StyledMedalIcon $position={medalPosition} />
          ) : (
            <DataText>{index}</DataText>
          )}
        </OrderNumber>
        <UserAliasAvatar name={question.userAlias} />
        <UserAlias $hasSpaces={question.userAlias.includes(' ')}>
          {question.userAlias}
        </UserAlias>

        <ChangePositionIconWrapper>
          <StyledDisplayUpIcon $show={internalPositionChange === PositionChange.UP} />

          <StyledDisplayDownIcon $show={internalPositionChange === PositionChange.DOWN} />
        </ChangePositionIconWrapper>
      </Wrapper>
    );
  },
);
